import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEmailAccounts } from "../hooks/useEmailAccounts.js";
import {
  TERMINAL_SCAN_STATUSES,
  useScanStatus,
  useStartScan,
} from "../hooks/useScanJob.js";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ScanDialog({ open, onClose }: Props) {
  const accounts = useEmailAccounts();
  const start = useStartScan();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(() => isoDate(-30));
  const [to, setTo] = useState(() => isoDate(0));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [jobId, setJobId] = useState<string | null>(null);
  const status = useScanStatus(jobId);

  // Po přechodu scanu do terminálního stavu načti znova faktury (a účty —
  // `last_scanned_at` se mění). useEffect se spustí jen na změnu statusu
  // (string), takže invalidace proběhne přesně jednou — při dokončení.
  const currentStatus = status.data?.status ?? null;
  useEffect(() => {
    if (currentStatus && TERMINAL_SCAN_STATUSES.has(currentStatus)) {
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["email-accounts"] });
    }
  }, [currentStatus, queryClient]);

  if (!open) return null;

  const accountList = accounts.data ?? [];

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onStart = async () => {
    if (selected.size === 0) {
      alert("Vyber alespoň jeden účet.");
      return;
    }
    const res = await start.mutateAsync({
      from,
      to,
      accountIds: Array.from(selected),
    });
    setJobId(res.jobId);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Spustit scan e-mailů</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            ✕
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col text-xs text-slate-500 gap-1">
            Datum od
            <input
              type="date"
              className="border border-slate-300 rounded px-2 py-1"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="flex flex-col text-xs text-slate-500 gap-1">
            Datum do
            <input
              type="date"
              className="border border-slate-300 rounded px-2 py-1"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
        </div>

        <div>
          <div className="text-xs text-slate-500 mb-1">
            E-mailové účty (více lze zaškrtnout)
          </div>
          {accountList.length === 0 && (
            <div className="text-sm text-slate-400">
              Žádné aktivní účty — nakonfiguruj v DB tabulce <code>email_accounts</code>.
            </div>
          )}
          <ul className="max-h-48 overflow-auto border border-slate-200 rounded">
            {accountList.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={selected.has(a.id)}
                  onChange={() => toggle(a.id)}
                />
                <div className="flex-1">
                  <div className="text-sm">{a.display_name}</div>
                  <div className="text-xs text-slate-500">
                    {a.protocol} · složky: {a.folders_to_scan.join(", ")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {status.data && (
          <div className="text-sm bg-slate-50 border border-slate-200 rounded p-2 space-y-1">
            <div>
              Status: <strong>{status.data.status}</strong>
            </div>
            <div>
              Zpracováno {status.data.processedEmails} / {status.data.totalEmails} ·
              Faktur extrahováno: {status.data.extractedInvoices}
            </div>
            {status.data.errors.length > 0 && (
              <div className="text-red-600">
                {status.data.errors.length} chyb
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border border-slate-300 rounded"
          >
            Zavřít
          </button>
          <button
            onClick={onStart}
            disabled={start.isPending}
            className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {start.isPending ? "Startuji…" : "Spustit scan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function isoDate(deltaDays: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}
